import { useState, useCallback, useEffect } from "react";
import { motion } from "framer-motion";

interface Feature {
  id: number;
  icon: string;
  title: string;
  color: string;
  image?: string;
  images?: string[];
  detailContent: string;
  benefits?: string[];
}

interface FeatureDetailModalProps {
  feature: Feature | null;
  onClose: () => void;
}

export default function FeatureDetailModal({
  feature,
  onClose,
}: FeatureDetailModalProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const imageList =
    feature?.images && feature.images.length > 0
      ? feature.images
      : feature?.image
      ? [feature.image]
      : [];

  const hasMultiple = imageList.length > 1;

  useEffect(() => {
    setCurrentIndex(0);
  }, [feature?.id]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? imageList.length - 1 : prev - 1));
  }, [imageList.length]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === imageList.length - 1 ? 0 : prev + 1));
  }, [imageList.length]);

  useEffect(() => {
    if (!feature) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [feature, goPrev, goNext, onClose]);

  if (!feature) return null;

  return (
    // Overlay — fade in/out
    <motion.div
      key="overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      onClick={onClose}
      style={{ backgroundColor: "rgba(15, 23, 42, 0.68)", backdropFilter: "blur(4px)" }}
    >
      {/* 모달 카드 — 카드와 동일한 layoutId로 shared layout animation */}
      <motion.div
        layoutId={`feature-card-${feature.id}`}
        layout="position"
        className="bg-white/[0.08] backdrop-blur-sm border border-white/20 rounded-3xl w-full shadow-2xl relative overflow-hidden flex flex-col md:flex-row"
        style={{ maxWidth: "1100px", maxHeight: "88vh" }}
        onClick={(e) => e.stopPropagation()}
        transition={{ type: "spring", stiffness: 520, damping: 42, mass: 0.7 }}
      >
        {/* --- 왼쪽: 텍스트 컨텐츠 (40%) --- */}
        <div className="md:w-[40%] p-8 md:p-10 flex flex-col justify-center overflow-y-auto shrink-0">
          {/* Icon — layoutId로 함께 이동 */}
          <motion.div
            layoutId={`feature-icon-${feature.id}`}
            className={`w-12 h-12 rounded-xl ${feature.color} flex items-center justify-center mb-5 shadow-lg`}
          >
            <i className={`${feature.icon} text-xl`} />
          </motion.div>

          {/* Title — layoutId로 함께 이동 */}
          <motion.h3
            layoutId={`feature-title-${feature.id}`}
            className="text-2xl font-bold text-white mb-4"
          >
            {feature.title}
          </motion.h3>

          {/* 나머지 콘텐츠는 딜레이 후 fade in */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.12 }}
          >
            <p className="text-white/65 leading-relaxed mb-7 text-sm">
              {feature.detailContent}
            </p>

            {feature.benefits && (
              <div className="grid grid-cols-1 gap-3">
                {feature.benefits.map((benefit, i) => (
                  <li
                    key={i}
                    className="flex items-center gap-3 text-sm text-white/75 list-none"
                  >
                    <i className="ri-checkbox-circle-fill text-blue-400 text-lg shrink-0" />
                    {benefit}
                  </li>
                ))}
              </div>
            )}
          </motion.div>
        </div>

        {/* --- 오른쪽: 이미지 슬라이더 (60%) --- */}
        {imageList.length > 0 && (
          <motion.div
            className="md:w-[60%] flex flex-col p-4 md:p-6 border-l border-white/10 overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.02, duration: 0.16 }}
          >
            {/* 닫기 버튼 */}
            <div className="flex justify-end mb-3 shrink-0">
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-full transition-all cursor-pointer"
              >
                <i className="ri-close-line text-base" />
              </button>
            </div>

            {/* 이미지 영역 */}
            <div className="w-full rounded-xl overflow-hidden bg-white/90 relative shrink-0 h-[360px] md:h-[400px] flex items-center justify-center p-3">
              <img
                key={currentIndex}
                src={imageList[currentIndex]}
                alt={`${feature.title} - ${currentIndex + 1}`}
                className="max-w-full max-h-full object-contain rounded-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2 }}
              />
            </div>

            {/* 하단 컨트롤 */}
            {hasMultiple && (
              <div className="flex items-center justify-between w-full mt-4 px-1 shrink-0">
                {/* 도트 인디케이터 */}
                <div className="flex items-center gap-1.5">
                  {imageList.length <= 8 ? (
                    imageList.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setCurrentIndex(i)}
                        className={`rounded-full transition-all cursor-pointer ${
                          i === currentIndex
                            ? "w-6 h-2 bg-white"
                            : "w-2 h-2 bg-white/35 hover:bg-white/55"
                        }`}
                      />
                    ))
                  ) : (
                    <>
                      {[0, 1, 2].map((i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentIndex(i)}
                          className={`rounded-full transition-all cursor-pointer ${
                            i === currentIndex
                              ? "w-6 h-2 bg-white"
                              : "w-2 h-2 bg-white/35 hover:bg-white/55"
                          }`}
                        />
                      ))}
                      <span className="text-white/40 text-xs mx-0.5">···</span>
                      {[
                        imageList.length - 3,
                        imageList.length - 2,
                        imageList.length - 1,
                      ].map((i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentIndex(i)}
                          className={`rounded-full transition-all cursor-pointer ${
                            i === currentIndex
                              ? "w-6 h-2 bg-white"
                              : "w-2 h-2 bg-white/35 hover:bg-white/55"
                          }`}
                        />
                      ))}
                    </>
                  )}
                  <span className="text-white/40 text-xs ml-2">
                    {currentIndex + 1} / {imageList.length}
                  </span>
                </div>

                {/* 화살표 버튼 */}
                <div className="flex gap-2">
                  <button
                    onClick={goPrev}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line text-lg" />
                  </button>
                  <button
                    onClick={goNext}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-all cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line text-lg" />
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  );
}
